import { useCallback, useMemo, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import { encodeUrlArrayParam } from 'utils/queryParamsUtil';

import {
  ALL_OFFERS_FILTER_KEYS,
  DEFAULT_SCHEDULE_SELECTION,
  DEFAULT_STATUS_SELECTION,
  URL_EMPTY_SELECTION
} from '../OfferFilters.consts';
import {
  IOffersFilterState,
  OfferPriceBucket,
  OfferScheduleType,
  OfferStatusFilterValue,
  OffersFilterKey
} from '../OfferFilters.types';
import {
  hasAnyActiveFilter,
  narrowToEnum,
  toggleArrayValue
} from '../OfferFilters.utils';

interface IUseOffersFiltersReturn {
  filters: IOffersFilterState;
  hasActiveFilters: boolean;
  setSearch: (value: string) => void;
  setStatuses: (values: OfferStatusFilterValue[]) => void;
  toggleStatus: (value: OfferStatusFilterValue) => void;
  setSchedules: (values: OfferScheduleType[]) => void;
  toggleSchedule: (value: OfferScheduleType) => void;
  setOfferDesignIds: (ids: string[]) => void;
  toggleOfferDesignId: (id: string) => void;
  setCreatedAtRange: (from: string, until: string) => void;
  setPriceBuckets: (buckets: OfferPriceBucket[]) => void;
  togglePriceBucket: (bucket: OfferPriceBucket) => void;
  clearAllFilters: () => void;
}

export const useOffersFilters = (): IUseOffersFiltersReturn => {
  const navigate = useNavigate();
  const location = useLocation();

  const urlParams = usePaginationFromURL('offers');

  const locationRef = useRef(location);
  const navigateRef = useRef(navigate);
  locationRef.current = location;
  navigateRef.current = navigate;

  const filters: IOffersFilterState = useMemo(() => {
    const isUserEmpty = (raw: string[]): boolean =>
      raw.length === 1 && raw[0] === URL_EMPTY_SELECTION;
    const urlStatusesTyped = narrowToEnum(
      urlParams.offerStatuses,
      OfferStatusFilterValue
    );
    const urlSchedulesTyped = narrowToEnum(
      urlParams.offerSchedules,
      OfferScheduleType
    );
    return {
      search: urlParams.search,
      statuses: isUserEmpty(urlParams.offerStatuses)
        ? []
        : urlStatusesTyped.length > 0
          ? urlStatusesTyped
          : [...DEFAULT_STATUS_SELECTION],
      schedules: isUserEmpty(urlParams.offerSchedules)
        ? []
        : urlSchedulesTyped.length > 0
          ? urlSchedulesTyped
          : [...DEFAULT_SCHEDULE_SELECTION],
      offerDesignIds: urlParams.offerDesignIds,
      createdAtFrom: urlParams.createdAtFrom,
      createdAtUntil: urlParams.createdAtUntil,
      priceBuckets: narrowToEnum(urlParams.priceBuckets, OfferPriceBucket)
    };
  }, [
    urlParams.search,
    urlParams.offerStatuses,
    urlParams.offerSchedules,
    urlParams.offerDesignIds,
    urlParams.createdAtFrom,
    urlParams.createdAtUntil,
    urlParams.priceBuckets
  ]);

  const hasActiveFilters = useMemo(() => hasAnyActiveFilter(filters), [filters]);

  const updateUrlParams = useCallback(
    (updates: Array<[string, string | null]>): void => {
      const queryParams = new URLSearchParams(locationRef.current.search);
      for (const [key, value] of updates) {
        if (value === null || value === '') {
          queryParams.delete(key);
        } else {
          queryParams.set(key, value);
        }
      }
      queryParams.set('page', '0');
      navigateRef.current(
        `${locationRef.current.pathname}?${queryParams.toString()}`,
        { replace: true }
      );
    },
    []
  );

  const setParam = useCallback(
    (key: string, value: string | null): void =>
      updateUrlParams([[key, value]]),
    [updateUrlParams]
  );

  const setArrayParam = useCallback(
    (key: string, values: string[]): void => {
      if (values.length === 0) {
        setParam(key, null);
        return;
      }
      setParam(key, encodeUrlArrayParam(values));
    },
    [setParam]
  );

  const setSearch = useCallback(
    (value: string): void => {
      setParam(OffersFilterKey.SEARCH, value || null);
    },
    [setParam]
  );

  const isDefaultSelection = useCallback(
    <T extends string>(values: T[], defaults: T[]): boolean =>
      values.length === defaults.length &&
      defaults.every((v: T): boolean => values.includes(v)),
    []
  );

  const setStatuses = useCallback(
    (values: OfferStatusFilterValue[]): void => {
      if (isDefaultSelection(values, DEFAULT_STATUS_SELECTION)) {
        setParam(OffersFilterKey.STATUSES, null);
        return;
      }
      if (values.length === 0) {
        setParam(OffersFilterKey.STATUSES, URL_EMPTY_SELECTION);
        return;
      }
      setArrayParam(OffersFilterKey.STATUSES, values);
    },
    [setParam, setArrayParam, isDefaultSelection]
  );

  const setSchedules = useCallback(
    (values: OfferScheduleType[]): void => {
      if (isDefaultSelection(values, DEFAULT_SCHEDULE_SELECTION)) {
        setParam(OffersFilterKey.SCHEDULES, null);
        return;
      }
      if (values.length === 0) {
        setParam(OffersFilterKey.SCHEDULES, URL_EMPTY_SELECTION);
        return;
      }
      setArrayParam(OffersFilterKey.SCHEDULES, values);
    },
    [setParam, setArrayParam, isDefaultSelection]
  );

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const toggleStatus = useCallback(
    (value: OfferStatusFilterValue): void => {
      setStatuses(toggleArrayValue(filtersRef.current.statuses, value));
    },
    [setStatuses]
  );

  const toggleSchedule = useCallback(
    (value: OfferScheduleType): void => {
      setSchedules(toggleArrayValue(filtersRef.current.schedules, value));
    },
    [setSchedules]
  );

  const setOfferDesignIds = useCallback(
    (ids: string[]): void => {
      setArrayParam(OffersFilterKey.OFFER_DESIGN_IDS, ids);
    },
    [setArrayParam]
  );

  const toggleOfferDesignId = useCallback(
    (id: string): void => {
      setOfferDesignIds(toggleArrayValue(filtersRef.current.offerDesignIds, id));
    },
    [setOfferDesignIds]
  );

  const setCreatedAtRange = useCallback(
    (from: string, until: string): void =>
      updateUrlParams([
        [OffersFilterKey.CREATED_AT_FROM, from || null],
        [OffersFilterKey.CREATED_AT_UNTIL, until || null]
      ]),
    [updateUrlParams]
  );

  const setPriceBuckets = useCallback(
    (buckets: OfferPriceBucket[]): void => {
      setArrayParam(OffersFilterKey.PRICE_BUCKETS, buckets);
    },
    [setArrayParam]
  );

  const togglePriceBucket = useCallback(
    (bucket: OfferPriceBucket): void => {
      setPriceBuckets(toggleArrayValue(filtersRef.current.priceBuckets, bucket));
    },
    [setPriceBuckets]
  );

  const clearAllFilters = useCallback((): void => {
    const queryParams = new URLSearchParams(locationRef.current.search);
    ALL_OFFERS_FILTER_KEYS.forEach((k: OffersFilterKey): void =>
      queryParams.delete(k)
    );
    queryParams.set('page', '0');
    navigateRef.current(
      `${locationRef.current.pathname}?${queryParams.toString()}`,
      { replace: true }
    );
  }, []);

  return {
    filters,
    hasActiveFilters,
    setSearch,
    setStatuses,
    toggleStatus,
    setSchedules,
    toggleSchedule,
    setOfferDesignIds,
    toggleOfferDesignId,
    setCreatedAtRange,
    setPriceBuckets,
    togglePriceBucket,
    clearAllFilters
  };
};
